import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DotsThree, DownloadSimpleIcon } from "@phosphor-icons/react";
import { AppButton, ConfirmationModal, FigmaTypography, LoadingSpinner } from "@libs/zapi-ui/custom";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments/useDocuments";
import { TDocument } from "@/zapi-frontend/shared/hooks/useDocuments/types";
import { formatCharacterLength } from "@/zapi-frontend/shared/lib/formatCharacterLength";
import { downloadVaultAll } from "@/zapi-frontend/shared/lib/downloadVaultAll";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { getMalwareScanMessage } from "@/zapi-frontend/shared/lib/malwareScanError";
import $api from "@/zapi-frontend/shared/api/axios";
import { UploadFilesButton } from "../Repository/_UploadFilesButton";
import { ProjectLoadingScreen } from "../ProjectLoadingScreen";

function fileExtension(doc: TDocument): string {
  if (doc.webUrl) return "LINK";
  const match = /\.([a-z0-9]+)$/i.exec(doc.fileName || "");
  return (match?.[1] || "FILE").toUpperCase();
}

function FileTypeBadge({ label }: { label: string }) {
  return (
    <div className="relative h-9 w-7 shrink-0 rounded-[5px] border border-innoveraZinc-150 overflow-hidden">
      <span className="absolute inset-x-[-1px] bottom-[3px] text-center text-[8px] leading-[10px] font-medium tracking-[-0.16px] text-innoveraZinc-500">
        {label}
      </span>
    </div>
  );
}

function VaultRow({
  doc,
  projectId,
  onDelete,
  onRetry
}: {
  doc: TDocument;
  projectId: string;
  onDelete: () => void;
  onRetry: () => void;
}) {
  const { t } = useTranslation("projectHome");
  const [isDownloading, setIsDownloading] = useState(false);
  const isWebLink = !!doc.webUrl;

  const handleDownload = useCallback(async () => {
    if (isWebLink) {
      window.open(doc.webUrl as string, "_blank");
      return;
    }
    setIsDownloading(true);
    const [response, error] = await tryCatch(
      $api.get<{ data: { url: string } }>(`/projects/${projectId}/documents/${doc.id}`)
    );
    const url = response?.data.data.url;
    if (!url) {
      setIsDownloading(false);
      toast.error(getMalwareScanMessage(error) ?? t("vaultPage.downloadFailed"));
      return;
    }
    const [blob] = await tryCatch(fetch(url).then((r) => r.blob()));
    if (!blob) {
      setIsDownloading(false);
      toast.error(t("vaultPage.downloadFailed"));
      return;
    }
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = doc.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
    setIsDownloading(false);
  }, [doc.fileName, doc.id, doc.webUrl, isWebLink, projectId, t]);

  const contentLength = doc.fullContentDisplay?.length || 0;

  return (
    <div className="flex items-center justify-between w-full py-3 pr-2 border-b border-innoveraZinc-150 bg-white">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        <FileTypeBadge label={fileExtension(doc)} />
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-[14px] leading-5 font-medium text-innoveraZinc-900 truncate">{doc.fileName}</span>
          <span className="flex items-center gap-1 w-full min-w-0 text-[12px] leading-[18px] text-innoveraZinc-500">
            {doc.status === "processed" && contentLength > 0 && (
              <span className="truncate min-w-0">
                {t("vaultPage.fullVersionChars", { size: formatCharacterLength(contentLength).replace(/ chars$/, "") })}
              </span>
            )}
            {doc.status === "processed" && contentLength === 0 && isWebLink && (
              <span className="truncate min-w-0">{doc.webUrl}</span>
            )}
            {(doc.status === "processing" || doc.status === "pending") && (
              <span className="inline-flex items-center gap-1">
                <LoadingSpinner />
                {t("vaultPage.processing")}
              </span>
            )}
            {doc.status === "error" && <span className="text-innoveraRed-400">{t("vaultPage.processFailed")}</span>}
          </span>
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="size-6 shrink-0 flex items-center justify-center rounded-md text-innoveraZinc-600 hover:bg-innoveraZinc-100 outline-none"
          >
            {isDownloading ? <LoadingSpinner /> : <DotsThree className="size-4" weight="bold" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem data-testid={`vault-row-${doc.id}-download-item`} onClick={handleDownload}>
            {isWebLink ? t("vaultPage.openLink") : t("vaultPage.download")}
          </DropdownMenuItem>
          {doc.status === "error" && (
            <DropdownMenuItem data-testid={`vault-row-${doc.id}-retry-item`} onClick={onRetry}>
              {t("vaultPage.retryProcessing")}
            </DropdownMenuItem>
          )}
          <DropdownMenuItem data-testid={`vault-row-${doc.id}-delete-item`} onClick={onDelete} variant="destructive">
            {t("vaultPage.delete")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function VaultIsland() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { id } = useParams();
  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);

  const {
    documents,
    isLoading,
    createDocument,
    createWebLink,
    deleteDocument,
    isDocumentsPending,
    isDocumentsProcessing,
    uploadingDocuments,
    setUploadingDocuments,
    retryJuicing
  } = useDocuments(projectId, undefined, 3000);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const handleDownloadAll = useCallback(async () => {
    setIsDownloadingAll(true);
    const [, error] = await tryCatch(downloadVaultAll(projectId));
    if (error) toast.error(t("vaultPage.downloadAllFailed"));
    setIsDownloadingAll(false);
  }, [projectId, t]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId === null) return;
    const id = deleteId;
    setDeleteId(null);
    deleteDocument(id);
  }, [deleteId, deleteDocument]);

  return (
    <div className="relative w-full min-w-0 h-full bg-white flex flex-col shadow-[0px_51px_14px_0px_rgba(24,24,27,0),0px_33px_13px_0px_rgba(24,24,27,0),0px_18px_11px_0px_rgba(24,24,27,0.01),0px_8px_8px_0px_rgba(24,24,27,0.01),0px_2px_5px_0px_rgba(24,24,27,0.02)]">
      <div className="h-14 shrink-0 border-b border-innoveraZinc-100 flex items-center px-4">
        <div className="flex-1 min-w-0">
          <span className="text-[16px] leading-[15px] font-medium text-innoveraZinc-900">{t("sidebar.vault")}</span>
        </div>
        <div className="flex items-center gap-2">
          <AppButton
            appearance="outlined"
            color="zinc150"
            className="text-innoveraZinc-900 shadow-none"
            onClick={handleDownloadAll}
            isLoading={isDownloadingAll}
            loadingText={t("vaultPage.downloading")}
            disabled={documents.length === 0}
            startAdornment={<DownloadSimpleIcon className="size-4" />}
            data-testid="vault-page-download-all"
          >
            {t("vaultPage.downloadAll")}
          </AppButton>
          <UploadFilesButton
            variant="header"
            createDocument={(file: File) => createDocument(file)}
            createWebLink={(url: string) => createWebLink(url)}
            setUploadingDocuments={setUploadingDocuments}
            isDocumentsPending={isDocumentsPending}
            isDocumentsProcessing={isDocumentsProcessing}
            existingWebUrls={documents.map((d) => d.webUrl)}
          />
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        {isLoading && documents.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <ProjectLoadingScreen />
          </div>
        ) : documents.length === 0 && uploadingDocuments.length === 0 ? (
          <div className="h-full flex items-center justify-center">
            <FigmaTypography variant="body-3" color="zinc500">
              {t("vaultPage.empty")}
            </FigmaTypography>
          </div>
        ) : (
          <div className="flex flex-col w-full">
            {documents.map((doc) => (
              <VaultRow
                key={doc.id}
                doc={doc}
                projectId={projectId}
                onDelete={() => setDeleteId(doc.id)}
                onRetry={() => retryJuicing(projectId, doc.id.toString())}
              />
            ))}
            {uploadingDocuments.length > 0 && (
              <div className="flex items-center gap-2 py-3 text-[12px] leading-[18px] text-innoveraZinc-500">
                <LoadingSpinner />
                <span className="truncate">{t("vaultPage.uploading", { files: uploadingDocuments.join(", ") })}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title={t("vaultPage.deleteTitle")}
        description={t("vaultPage.deleteDescription")}
        confirmText={t("vaultPage.delete")}
        cancelText={t("common:cancel")}
      />
    </div>
  );
}
